import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Progress } from '../ui/progress';
import { importJobsAPI } from '../../lib/api';
import { toast } from 'sonner';

export const BulkUploadModal = ({
  isOpen,
  onClose,
  bulkUploadFile,
  setBulkUploadFile,
  bulkUploadResults,
  setBulkUploadResults,
  uploadingBulk,
  setUploadingBulk,
  onUpload,
  onDownloadTemplate,
  onSuccess
}) => {
  const fileInputRef = useRef(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0, success: 0, failed: 0 });
  const [status, setStatus] = useState(null); // pending, processing, completed, failed, cancelled
  const eventSourceRef = useRef(null);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
        toast.error('Please select an Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setBulkUploadFile(file);
      setBulkUploadResults(null);
      setProgress({ processed: 0, total: 0, success: 0, failed: 0 });
      setStatus(null);
      setJobId(null);
    }
  };

  const handleClose = () => {
    // Close SSE connection if active
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setBulkUploadFile(null);
    setBulkUploadResults(null);
    setProgress({ processed: 0, total: 0, success: 0, failed: 0 });
    setStatus(null);
    setJobId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const startSSEPolling = (jId) => {
    // Use polling instead of SSE for better reliability
    // Auth is handled via httpOnly cookies, no token needed
    const pollStatus = async () => {
      try {
        const response = await importJobsAPI.getJobStatus(jId);
        const data = response.data;
        
        setStatus(data.status);
        if (data.progress) {
          setProgress(data.progress);
        }
        
        if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
          if (setUploadingBulk) setUploadingBulk(false);
          
          if (data.results) {
            setBulkUploadResults({
              success: data.results.success || 0,
              failed: data.results.failed || 0,
              errors: data.results.errors || [],
              total: data.results.total || 0
            });
          }
          
          if (data.status === 'completed') {
            toast.success(`Import completed: ${data.results?.success || 0} employees imported`);
            if (onSuccess) onSuccess();
          } else if (data.status === 'failed') {
            toast.error(`Import failed: ${data.error || 'Unknown error'}`);
          }
          return; // Stop polling
        }
        
        // Continue polling
        setTimeout(pollStatus, 1000);
      } catch (error) {
        console.error('Polling error:', error);
        // Retry after delay
        setTimeout(pollStatus, 2000);
      }
    };
    
    pollStatus();
  };

  const handleAsyncUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a file first');
      return;
    }

    if (setUploadingBulk) setUploadingBulk(true);
    setStatus('pending');

    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);

      const response = await importJobsAPI.startAsyncImport(formData);
      const { job_id } = response.data;
      
      setJobId(job_id);
      toast.info('Import job started. Processing in background...');
      
      // Start polling for status
      startSSEPolling(job_id);
      
    } catch (error) {
      if (setUploadingBulk) setUploadingBulk(false);
      setStatus('failed');
      
      let errorMessage = 'Upload failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        else if (typeof detail === 'object') errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    }
  };

  const handleCancelJob = async () => {
    if (!jobId) return;
    
    try {
      await importJobsAPI.cancelJob(jobId);
      toast.info('Import job cancellation requested');
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk Upload Employees</DialogTitle>
          <DialogDescription>
            Upload an Excel file to import multiple employees at once. Large imports are processed in the background.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Download Template */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Download Template First</p>
                <p className="text-sm text-blue-700 mt-1">
                  Use the template to ensure your data is in the correct format
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onDownloadTemplate}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* File Upload - only show if not processing */}
          {!status || status === 'failed' ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Select File</label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="flex-1"
                  data-testid="bulk-upload-file-input"
                />
              </div>
              {bulkUploadFile && (
                <p className="text-sm text-slate-600">
                  Selected: <span className="font-medium">{bulkUploadFile.name}</span>
                </p>
              )}
            </div>
          ) : null}

          {/* Progress Section */}
          {(status === 'pending' || status === 'processing') && (
            <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {status === 'pending' ? 'Starting import...' : 'Processing import...'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {progress.processed} of {progress.total} records processed
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleCancelJob}>
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
              
              <Progress value={progressPercent} className="h-2" />
              
              <div className="flex justify-between text-sm">
                <span className="text-emerald-600">{progress.success} successful</span>
                <span className="text-slate-500">{progressPercent}%</span>
                <span className="text-red-600">{progress.failed} failed</span>
              </div>
            </div>
          )}

          {/* Upload Results */}
          {bulkUploadResults && (status === 'completed' || status === 'failed' || status === 'cancelled') && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{bulkUploadResults.success}</p>
                  <p className="text-xs text-emerald-600">Imported Successfully</p>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                  <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-red-700">{bulkUploadResults.failed}</p>
                  <p className="text-xs text-red-600">Failed to Import</p>
                </div>
              </div>

              {bulkUploadResults.errors && bulkUploadResults.errors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-800 mb-2">Errors (showing first 10):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {bulkUploadResults.errors.slice(0, 10).map((err, index) => (
                      <p key={typeof err === 'object' && err.row ? `error-row-${err.row}` : `error-${index}`} className="text-sm text-red-700">
                        {typeof err === 'string' ? err : `Row ${err.row}: ${err.error}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {bulkUploadResults ? 'Close' : 'Cancel'}
          </Button>
          {(!status || status === 'failed') && !bulkUploadResults && (
            <Button 
              onClick={handleAsyncUpload} 
              disabled={!bulkUploadFile || uploadingBulk}
              data-testid="bulk-upload-submit-btn"
            >
              {uploadingBulk ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
